import { useState, useEffect, useCallback } from 'react';
import { Shield } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/app/components/ui/tabs';
import { usePermissions, useRequireRole } from '@/hooks/use-permissions';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { PeopleTab } from './people-tab';
import { JobsTab } from './jobs-tab';
import { PermissionsTab } from './permissions-tab';
import { ActivityTab } from './activity-tab';

const VALID_TABS = ['people', 'jobs', 'permissions', 'activity'] as const;
const MANAGER_TABS = ['people', 'jobs'] as const;
type AdminTab = (typeof VALID_TABS)[number];

function getTabFromHash(hash: string, isAdmin: boolean): AdminTab {
  const value = hash.replace('#', '') as AdminTab;
  const allowedTabs: readonly AdminTab[] = isAdmin ? VALID_TABS : MANAGER_TABS;
  return allowedTabs.includes(value) ? value : 'people';
}

export function AdminDashboardPage() {
  const hasAccess = useRequireRole(['Admin', 'Manager']);
  const { isAdmin } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<AdminTab>(() =>
    getTabFromHash(location.hash, isAdmin)
  );

  // Sync tab from URL hash on navigation
  useEffect(() => {
    const tabFromHash = getTabFromHash(location.hash, isAdmin);
    if (tabFromHash !== activeTab) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveTab(tabFromHash);
    }
    // Only react to hash changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, isAdmin]);

  const handleTabChange = useCallback(
    (value: string) => {
      const tab = value as AdminTab;
      setActiveTab(tab);
      navigate({ hash: tab }, { replace: true });
    },
    [navigate]
  );

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100">
          <Shield className="h-5 w-5 text-blue-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-500">
            Manage your organization&apos;s people and projects
            {isAdmin ? ', permissions, and activity' : ''}.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="jobs">Projects</TabsTrigger>
          {isAdmin && <TabsTrigger value="permissions">Permissions</TabsTrigger>}
          {isAdmin && <TabsTrigger value="activity">Activity</TabsTrigger>}
        </TabsList>
        <TabsContent value="people">
          <PeopleTab />
        </TabsContent>
        <TabsContent value="jobs">
          <JobsTab />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="permissions">
            <PermissionsTab />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="activity">
            <ActivityTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
